// Chi tiết Job (Mint-JobDetail): kiểu, dữ liệu mẫu bản ghi vàng SE260637420 và logic mốc / cut-off. Không có tiền.
import { HANA_CLIENT_INFO, configFromClientInfo, createWorkCalendar } from '@hana/work-calendar';
import type {} from '@hana/data';
import type { ShipmentStatus } from './types';

export interface JobContainer {
  /** số cont, vd "TGHU 812 345-7" (kiểm ISO 6346) */
  no: string;
  type: string;
  seal: string;
  /** VGM kg */
  vgm: number;
  /** lấy rỗng, vd "20/Oct 14:10" */
  pickup: string;
  /** hạ bãi: thời điểm hoặc null khi chưa hạ */
  gateIn: string | null;
  /** ghi chú free time (ngày làm việc còn lại) */
  freeNote: string;
}

export interface JobPerson {
  user: string;
  role: string;
}

/** Chi tiết một Job. `id` = JobID. */
export interface JobDetail {
  id: string;
  title: string;
  status: ShipmentStatus;
  hbl: string;
  mbl: string;
  bookingNo: string;
  carrier: string;
  vessel: string;
  voyage: string;
  pol: { code: string; name: string };
  pod: { code: string; name: string };
  /** ISO giờ VN */
  etd: string;
  eta: string;
  closeDoc: string;
  closeCY: string;
  confirmedAt: string;
  people: JobPerson[];
  parties: { label: string; value: string }[];
  cargo: { label: string; value: string }[];
  containers: JobContainer[];
  /** mốc đã hoàn tất: SI nộp, VGM khai (ISO) */
  siDoneAt: string | null;
  vgmDoneAt: string | null;
}

declare module '@hana/data' {
  interface Entities {
    jobDetail: JobDetail;
  }
}

export const JOB_DETAILS: readonly JobDetail[] = [
  {
    id: 'SE260637420',
    title: 'Sea FCL · Xuất · 2×40HC',
    status: 'BL Hold',
    hbl: 'HNSGN260637420',
    mbl: 'YMJAM490371125',
    bookingNo: 'YMJAM490371125',
    carrier: 'YML',
    vessel: 'YM CERTAINTY',
    voyage: '089N',
    pol: { code: 'VNSGN', name: 'HOCHIMINH CITY' },
    pod: { code: 'USLAX', name: 'LOS ANGELES' },
    etd: '2026-10-24T11:00+07:00',
    eta: '2026-11-08T06:00+07:00',
    closeDoc: '2026-10-21T16:00+07:00',
    closeCY: '2026-10-23T23:59+07:00',
    confirmedAt: '2026-10-15T09:12+07:00',
    people: [
      { user: 'DAVID', role: 'Sale' },
      { user: 'MAITTN', role: 'CS' },
      { user: 'NHINTT', role: 'Sale Asst' },
      { user: 'PHUONGNKT', role: 'BKG · nhận xử lý' },
    ],
    parties: [
      { label: 'Shipper', value: 'SAVITA' },
      { label: 'Consignee', value: 'Pacific Apparel LLC' },
      { label: 'Notify', value: 'Same as consignee' },
      { label: 'Đại lý đầu đến', value: 'Harbor Link Logistics (LAX)' },
      { label: 'Người trả cước', value: 'Shipper · Prepaid' },
      { label: 'Incoterm', value: 'FOB Cát Lái' },
      { label: 'Báo giá', value: 'Q26FDSGNLAX077 · khớp giá' },
      { label: 'Free time', value: 'DEM 5 · DET 7 ngày' },
      { label: 'Phụ phí', value: 'THC · AMS · BLF · TLX' },
      { label: 'Policy', value: 'Telex sau khi khách thanh toán' },
    ],
    cargo: [
      { label: 'Mô tả hàng', value: 'COIR ROPE' },
      { label: 'HS chính', value: '5607.90 · đã xác nhận' },
      { label: 'Số kiện', value: '412 BALE(S)' },
      { label: 'GW / NW', value: '18.420 / 17.900 kg' },
      { label: 'CBM', value: '118,4' },
      { label: 'Cont', value: '2×40HC' },
      { label: 'Nguồn', value: 'SI khách · myHANA' },
    ],
    containers: [
      { no: 'TGHU 812 345-7', type: '40HC', seal: 'YML 4428119', vgm: 9240, pickup: '20/Oct 14:10', gateIn: null, freeNote: 'Free time DET còn 2 ngày làm việc' },
      { no: 'YMLU 541 220-0', type: '40HC', seal: 'YML 4428120', vgm: 9180, pickup: '20/Oct 15:02', gateIn: '22/Oct 08:40', freeNote: 'Free time còn 2 ngày làm việc' },
    ],
    siDoneAt: '2026-10-20T16:40+07:00',
    vgmDoneAt: '2026-10-21T10:05+07:00',
  },
];

/** Kiểm số container theo ISO 6346 (4 chữ + 7 số, số cuối là số kiểm). */
export function isValidContainerNo(raw: string): boolean {
  const c = raw.replace(/[\s-]/g, '').toUpperCase();
  if (!/^[A-Z]{4}\d{7}$/.test(c)) return false;
  const value: Record<string, number> = {};
  let n = 10;
  for (const ch of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
    if (n % 11 === 0) n++;
    value[ch] = n++;
  }
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const ch = c.charAt(i);
    sum += (/\d/.test(ch) ? Number(ch) : (value[ch] ?? 0)) * 2 ** i;
  }
  return (sum % 11) % 10 === Number(c.charAt(10));
}

/** Số cont đã hạ bãi / tổng số cont. */
export function gateInCount(d: Pick<JobDetail, 'containers'>): { done: number; total: number } {
  return { done: d.containers.filter((c) => c.gateIn).length, total: d.containers.length };
}

/** Transit (ngày) giữa ETD và ETA. */
export function transitDays(d: Pick<JobDetail, 'etd' | 'eta'>): number {
  return Math.round((Date.parse(d.eta) - Date.parse(d.etd)) / 86_400_000);
}

const calendar = () => createWorkCalendar(configFromClientInfo(HANA_CLIENT_INFO));

/** Mốc neo giờ hãng tàu (SOP): hạn nội bộ nộp SI = CloseDoc − 9h, hạ bãi = CloseCY − 5h, tính theo giờ làm việc (work-calendar). */
export function internalDeadlines(d: Pick<JobDetail, 'closeDoc' | 'closeCY'>): { si: string; cy: string } {
  const cal = calendar();
  return { si: cal.subHours(d.closeDoc, 9), cy: cal.subHours(d.closeCY, 5) };
}

export type MilestoneState = 'done' | 'cur' | 'todo';
export interface Milestone {
  key: string;
  label: string;
  state: MilestoneState;
  /** thời điểm đã xong / hạn / dự kiến (ISO) */
  at: string | null;
  note: string;
}

/** Chuỗi mốc của job: Booking → SI → VGM → Hạ bãi CY → ATD → ATA (mốc đầu chưa xong là "đang tới"). */
export function milestones(d: JobDetail): Milestone[] {
  const gate = gateInCount(d);
  const gateDone = gate.total > 0 && gate.done === gate.total;
  const rows: { key: string; label: string; at: string | null; note: string; done: boolean }[] = [
    { key: 'bk', label: 'Booking', at: d.confirmedAt, note: 'BK Confirmed', done: true },
    { key: 'si', label: 'SI', at: d.siDoneAt ?? d.closeDoc, note: d.siDoneAt ? 'Đã nộp' : 'Hạn SI', done: !!d.siDoneAt },
    { key: 'vgm', label: 'VGM', at: d.vgmDoneAt, note: d.vgmDoneAt ? 'Đã khai' : 'Chưa khai', done: !!d.vgmDoneAt },
    { key: 'cy', label: 'Hạ bãi CY', at: d.closeCY, note: `${gate.done}/${gate.total} cont`, done: gateDone },
    { key: 'atd', label: 'ATD', at: d.etd, note: 'Dự kiến', done: false },
    { key: 'ata', label: 'ATA', at: d.eta, note: 'Dự kiến', done: false },
  ];
  let cur = false;
  return rows.map((r) => {
    const state: MilestoneState = r.done ? 'done' : cur ? 'todo' : ((cur = true), 'cur');
    return { key: r.key, label: r.label, state, at: r.at, note: r.note };
  });
}
